(() => {
  if (customElements.get('swe-orthoride-reveal')) return;
  customElements.define('swe-orthoride-reveal', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.stage = this.querySelector('.swe-cutaway__stage');
      this.image = this.querySelector('.swe-cutaway__base');
      this.button = this.querySelector('button');
      this.annotations = this.querySelector('.swe-cutaway__annotations');
      this.status = this.querySelector('[role="status"]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.imagesReady = false;
      this.failed = false;
      this.canAnimate = CSS.supports('mask-image', 'linear-gradient(black, transparent)') ||
        CSS.supports('-webkit-mask-image', 'linear-gradient(black, transparent)');
      this.waiting = this.canAnimate && !this.motion.matches && !this.played &&
        this.dataset.autoplay === 'true' && typeof IntersectionObserver !== 'undefined';
      this.draw(this.waiting ? 0 : 100);
      this.describe(!this.waiting);
      this.annotations.hidden = this.waiting;
      // Controls and autoplay wait for both layers, avoiding a reveal of an unloaded image.
      Promise.all([...this.querySelectorAll('img')].map(image => image.decode())).then(() => {
        if (signal.aborted) return;
        this.imagesReady = true;
        this.button.hidden = !this.canAnimate || this.motion.matches;
        if (this.waiting) {
          this.observer = new IntersectionObserver(entries => {
            if (entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= 0.45)) {
              this.play();
            }
          }, { threshold: 0.45 });
          this.observer.observe(this.stage);
        }
      }).catch(() => {
        if (signal.aborted) return;
        this.failed = true;
        this.finish();
      });
      this.button.addEventListener('click', () => this.play(), { signal });
      this.motion.addEventListener('change', () => {
        if (this.motion.matches) this.finish();
        else this.button.hidden = !this.imagesReady || !this.canAnimate;
      }, { signal });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden && this.frame) this.finish();
      }, { signal });
    }
    draw(value) {
      this.value = value;
      const edge = 8 + value * 0.36;
      this.stage.style.setProperty('--reveal-solid', `${edge - 1.5}%`);
      this.stage.style.setProperty('--reveal-clear', `${edge + 1.5}%`);
    }
    describe(shown, status) {
      this.image.setAttribute('alt', shown
        ? 'OrthoRide Western saddle pad with a localized cutaway showing its internal layers'
        : 'Original intact OrthoRide Western saddle pad');
      this.status.textContent = status || (shown ? 'Cutaway shown' : 'Intact pad');
    }
    play() {
      if (!this.imagesReady || !this.canAnimate || this.motion.matches) { this.finish(); return; }
      this.stop();
      this.observer?.disconnect();
      this.played = true;
      this.waiting = false;
      this.annotations.hidden = true;
      this.draw(0);
      const start = performance.now();
      this.describe(false, 'Revealing the cutaway');
      const tick = now => {
        const progress = Math.min((now - start) / 2000, 1);
        this.draw(100 * progress * progress * (3 - 2 * progress));
        if (progress === 1) { this.finish(); return; }
        this.frame = requestAnimationFrame(tick);
      };
      this.frame = requestAnimationFrame(tick);
    }
    finish() {
      this.stop();
      this.observer?.disconnect();
      this.played = true;
      this.waiting = false;
      this.draw(this.failed ? 0 : 100);
      this.describe(!this.failed);
      this.annotations.hidden = this.failed;
      this.button.hidden = !this.imagesReady || !this.canAnimate || this.motion.matches;
    }
    stop() { cancelAnimationFrame(this.frame); this.frame = 0; }
    disconnectedCallback() {
      this.stop();
      this.observer?.disconnect();
      this.controller?.abort();
      this.controller = null;
    }
  });
})();
